import { DETAIL_ADMIN_ONLY, NOT_MEASURED, agoLabel } from '../../utils/format'
import { HALT_CLEARED_MARKER, errorTextView, haltDetailView } from '../../utils/haltDetail'
import type { ErrorTextView } from '../../utils/haltDetail'
import { runOutcomeLook } from '../outcomeFormat'
import type { OutcomeLook } from '../outcomeFormat'
import type { ResumeRule } from '../../types/etlProgress'
import type { EtlHalt } from '../../types/etlHalts'
import type {
  EtlHaltSummary,
  EtlJob,
  EtlJobCatalog,
  EtlJobParameter,
  EtlJobTriggerRequest,
  EtlJobTriggerStatus,
} from '../../types/etlJobs'

/**
 * The Scheduler's pure logic: everything the page, the run form and the request history decide that can be
 * decided without React. Imports only utils/format (itself MSAL-free) and the pure components/outcomeFormat,
 * so it runs under vitest's node environment as-is.
 */

// ---------------------------------------------------------------------------------------------------------
// Page states
// ---------------------------------------------------------------------------------------------------------

/**
 * Which of four things the catalogue area shows. Failed, loading and "no jobs published" must not look
 * alike, and between retries React Query reads "not loading, not errored" with no data — which is loading,
 * not an empty catalogue.
 *
 * `failed` only when there is nothing to show: a background refetch that fails after a good load keeps the
 * list it has (the page says the refresh failed beside it) rather than blanking the Scheduler.
 */
export function catalogView(
  catalog: EtlJobCatalog | undefined,
  isLoading: boolean,
  isError: boolean
): 'failed' | 'loading' | 'empty' | 'jobs' {
  if (isError && !catalog) return 'failed'
  if (isLoading || !catalog) return 'loading'
  return catalog.jobs.length === 0 ? 'empty' : 'jobs'
}

/**
 * Which of four things the request history shows. It used to render only when non-empty and to ignore the
 * error, so a 401 or a 500 read as "no requests". As with the catalogue, a failed refetch after a good load
 * keeps its rows (and the page says the refresh failed) — only an error with nothing loaded is `failed`.
 */
/** How many requests the Scheduler's history asks for. The hook's default and the caption both read it. */
export const REQUEST_HISTORY_LIMIT = 15

/**
 * The history's caption. The endpoint returns the newest `limit` and no total, so a list that filled the
 * limit says it is capped; a shorter one is everything on file.
 */
export function requestHistoryCaption(shown: number | null, limit: number = REQUEST_HISTORY_LIMIT): string {
  const base = "Everyone's, newest first — not only yours."
  if (shown == null || shown === 0) return base
  if (shown >= limit) return `${base} The latest ${limit} requests; older ones are not listed here.`
  return `${base} ${shown === 1 ? 'The one request' : `All ${shown} requests`} on file.`
}

export function requestHistoryView(
  requests: EtlJobTriggerRequest[] | undefined,
  isLoading: boolean,
  isError: boolean
): 'failed' | 'loading' | 'empty' | 'rows' {
  if (isError && !requests) return 'failed'
  if (isLoading || !requests) return 'loading'
  return requests.length === 0 ? 'empty' : 'rows'
}

/**
 * Why the server would refuse this job right now, or null: the caller's permission first (it refuses
 * everything), then an active halt (which refuses all but its exempt categories).
 *
 * `canRun` is the page's reading of the catalogue's permission — true when absent, for an older API.
 */
export function runRefusal(
  job: Pick<EtlJob, 'category'>,
  canRun: boolean,
  deniedReason: string | null | undefined,
  activeHalt: EtlHaltSummary | null | undefined
): string | null {
  if (!canRun) return deniedReason?.trim() ? deniedReason : 'You cannot run jobs from this session.'
  return haltRefusal(job.category, activeHalt)
}

// ---------------------------------------------------------------------------------------------------------
// Request history
// ---------------------------------------------------------------------------------------------------------

/**
 * What a request asked for, as `name=value` pairs in the order they were stored.
 *
 * Dates are shown exactly as recorded (plain yyyy-MM-dd, never re-parsed into an instant). No parameters,
 * or an older API that does not serve them, is a dash — never "defaults", which the page cannot know.
 */
export function formatRequestParameters(parameters: EtlJobTriggerRequest['parameters']): string {
  if (!parameters) return NOT_MEASURED
  const entries = Object.entries(parameters)
  if (entries.length === 0) return NOT_MEASURED
  return entries
    .map(([name, value]) => `${name}=${value == null || value === '' ? NOT_MEASURED : value}`)
    .join(' · ')
}

/** Where a request's run can be looked at, or null when no run is linked. */
export interface RequestRunLink {
  kind: 'data-comb' | 'entity-run' | 'execution'
  path: string
  label: string
  /** The page the link opens, for its tooltip. */
  destination: string
}

/**
 * How a linked run's outcome reads beside its request: the served outcome (never the raw status, which reads
 * Running for a dead worker's run), in the words and colour every other page gives it — components/
 * outcomeFormat.ts, the map Execution History reads, because the request's run link opens that page. Null
 * when no run is linked.
 */
export function requestRunOutcome(request: Pick<EtlJobTriggerRequest, 'etlRunOutcome'>): OutcomeLook | null {
  return request.etlRunOutcome ? runOutcomeLook(request.etlRunOutcome) : null
}

/**
 * The run a request caused, most specific first.
 *
 * A Data Comb pass opens the comb; an entity job opens its own entity row in the ETL Explorer (whose ids are
 * entity-run ids); anything else with a run opens the pass in Execution History. Null is NOT "no run": see
 * {@link runDashCaption} for what an unlinked request can mean.
 */
export function requestRunLink(request: EtlJobTriggerRequest): RequestRunLink | null {
  if (request.dataCombRunId) {
    return {
      kind: 'data-comb',
      path: `/data-comb?runId=${encodeURIComponent(request.dataCombRunId)}`,
      label: 'Comb pass',
      destination: 'Data Comb',
    }
  }
  if (request.entityRunId) {
    return {
      kind: 'entity-run',
      path: `/etl-explorer/${encodeURIComponent(request.entityRunId)}`,
      label: request.etlRunKey ?? 'Entity run',
      destination: 'ETL Explorer',
    }
  }
  if (request.etlRunId) {
    return {
      kind: 'execution',
      path: `/execution-history/${encodeURIComponent(request.etlRunId)}`,
      label: request.etlRunKey ?? 'Run',
      destination: 'Execution History',
    }
  }
  return null
}

/** Why a request's Run cell is a dash, by the request's status. */
export function runDashCaption(status: EtlJobTriggerStatus | string): string {
  switch (status) {
    case 'Enqueued':
      return (
        'No run recorded for this request yet. It may not have started; it may have been dropped because '
        + 'another load of the same kind was running (Hangfire shows it Succeeded and no run is written); '
        + 'it may have resumed an earlier run, whose record keeps that run\'s own Hangfire id; or it '
        + 'predates job-id capture.'
      )
    case 'Requested':
      return (
        'Automaton has not picked it up. If Automaton is down it will still run when it returns — '
        + 'requests do not expire.'
      )
    case 'Rejected':
    case 'Failed':
      return 'This request never reached Hangfire, so it started no run.'
    default:
      return 'No run is linked to this request.'
  }
}

/** Poll while a request is waiting to be claimed: Automaton claims one within seconds. */
export const REQUESTED_POLL_MS = 3_000
/** Poll while a claimed request has no run yet: the run is written only once the Hangfire job starts. */
export const UNRESOLVED_POLL_MS = 15_000
/** How long after it was claimed a request is still worth watching for its run. */
export const UNRESOLVED_WATCH_MS = 10 * 60_000

/**
 * How often the request history re-asks.
 *
 * It used to stop as soon as nothing was Requested — but the run a request causes appears only after its
 * Hangfire job starts, which is after the request reads Enqueued. So an Enqueued row with no run yet keeps
 * a slower poll going for ten minutes after it was claimed. After that it is either linked, or it will not
 * be (dropped, resumed, or predating job-id capture — see {@link runDashCaption}), and polling stops.
 */
export function triggerRequestsPollInterval(
  rows: EtlJobTriggerRequest[] | undefined,
  nowMs: number
): number | false {
  if (!rows || rows.length === 0) return false
  if (rows.some((r) => r.status === 'Requested')) return REQUESTED_POLL_MS

  const awaitingRun = rows.some((r) => {
    if (r.status !== 'Enqueued' || r.etlRunId || r.dataCombRunId) return false
    const claimedAt = Date.parse(r.processedAt ?? r.requestedAt)
    if (Number.isNaN(claimedAt)) return false
    // A claim stamped slightly in the future (clock skew) is recent, not old.
    return nowMs - claimedAt <= UNRESOLVED_WATCH_MS
  })

  return awaitingRun ? UNRESOLVED_POLL_MS : false
}

/** A toast, as notifications.show takes one. */
export interface Toast {
  title: string
  message: string
  color: string
}

/**
 * What to say once a request has been followed for as long as the form follows it.
 *
 * `latest` is the last state read, or null when no read succeeded. Enqueued is "queued", never "started":
 * Hangfire holds it until a worker is free, and it may yet be dropped by the one-at-a-time lock. A request
 * still Requested at the end is not lost — requests do not expire — so that says so rather than nothing.
 */
export function followUpToast(jobName: string, latest: EtlJobTriggerRequest | null): Toast {
  if (latest === null) {
    return {
      title: 'Could not follow the request',
      message: `${jobName} was requested, but reading its progress failed. Recent run requests shows what became of it.`,
      color: 'yellow',
    }
  }

  switch (latest.status) {
    case 'Enqueued':
      return {
        title: 'Queued',
        message: latest.hangfireJobId
          ? `${jobName} is queued as Hangfire job ${latest.hangfireJobId}. It has not necessarily started.`
          : `${jobName} is queued in Hangfire. It has not necessarily started.`,
        color: 'blue',
      }
    case 'Requested':
      return {
        title: 'Not picked up yet',
        message:
          'Automaton has not claimed this request in a minute; it may be down. The request stays queued '
          + 'and will run when it returns.',
        color: 'yellow',
      }
    default:
      return {
        title: `Run ${latest.status.toLowerCase()}`,
        message: requestStatusDetailText(latest) ?? `${jobName} did not start.`,
        color: 'red',
      }
  }
}

/**
 * A request's detail as this caller may see it. A Failed request's detail is a raw exception message the server
 * serves only to an administrator (D8); `statusDetailWithheld` says it was recorded. The request history renders
 * THIS rather than `statusDetail`, so a withheld detail reads DETAIL_ADMIN_ONLY rather than nothing.
 */
export function requestStatusDetailView(request: EtlJobTriggerRequest): ErrorTextView {
  return errorTextView(request.statusDetail, request.statusDetailWithheld)
}

/** The same, as one line: the detail, DETAIL_ADMIN_ONLY when withheld, or null when none was recorded. */
export function requestStatusDetailText(request: EtlJobTriggerRequest): string | null {
  const view = requestStatusDetailView(request)
  return view.kind === 'withheld' ? DETAIL_ADMIN_ONLY : view.kind === 'recorded' ? view.text : null
}

// ---------------------------------------------------------------------------------------------------------
// Deep link and the run form
// ---------------------------------------------------------------------------------------------------------

/** The query-string key naming the job; never read as a parameter value. */
export const JOB_QUERY_KEY = 'job'

/**
 * The run form's top notice for a deep link, or null for a form opened from its own Run button.
 *
 * The JOB is the link's choice too, not only its values: `/jobs?job=warehouse-etl` (or a link whose values
 * all equal the defaults) opens a form that marks no field, and without a notice it looks exactly like one
 * the operator opened themselves. So a link-opened form always says so.
 */
export function linkedAlertText(linkedCount: number, openedFromLink = false): string | null {
  if (linkedCount > 0) {
    return `Opened from a link: ${linkedCount} ${linkedCount === 1 ? 'value was' : 'values were'} pre-filled. `
      + 'Check them before running.'
  }
  if (openedFromLink) return 'Opened from a link: the link chose this job. Check the job before running.'
  return null
}

/** A link value the form would not use, and why. */
export interface IgnoredLinkValue {
  name: string
  value: string
  reason: string
}

/** The form's starting values for a job opened from a link, and where each came from. */
export interface LinkProvenance {
  /** The declared defaults, overridden by usable link values. */
  values: Record<string, string>
  /** Parameters whose value came from the link AND differs from its default — the ones to point out. */
  linkedNames: string[]
  /** Link values naming a declared parameter that the form could not show faithfully, so did not take. */
  ignored: IgnoredLinkValue[]
}

/**
 * Applies a deep link's query string to a job's declared defaults.
 *
 * Only names the job DECLARES are read (the catalogue's `startDate`, `endDate`, and `module` for the
 * warehouse load); every other query parameter is ignored, and so is an empty one. A link is somebody
 * else's choice arriving in your form, so a value that would be invisible in its field — an Enum value
 * that is not one of the options renders as a blank select while still being submitted, a Bool that is
 * neither true nor false — is refused and reported rather than taken.
 */
export function linkProvenance(
  parameters: EtlJobParameter[],
  defaults: Record<string, string>,
  search: URLSearchParams
): LinkProvenance {
  const values: Record<string, string> = { ...defaults }
  const linkedNames: string[] = []
  const ignored: IgnoredLinkValue[] = []

  for (const parameter of parameters) {
    if (parameter.name === JOB_QUERY_KEY) continue
    const raw = search.get(parameter.name)
    if (raw === null) continue
    const value = raw.trim()
    if (value === '') continue

    if (parameter.kind === 'Enum' && parameter.options && parameter.options.length > 0
        && !parameter.options.includes(value)) {
      ignored.push({ name: parameter.name, value, reason: 'it is not one of the options' })
      continue
    }
    if (parameter.kind === 'Bool' && value !== 'true' && value !== 'false') {
      ignored.push({ name: parameter.name, value, reason: 'it is neither true nor false' })
      continue
    }

    values[parameter.name] = value
    if (value !== (defaults[parameter.name] ?? '')) linkedNames.push(parameter.name)
  }

  return { values, linkedNames, ignored }
}

/** The parameter names the catalogue gives every ETL job's date window (JobSchedulerService). */
export const WINDOW_START = 'startDate'
export const WINDOW_END = 'endDate'

const PLAIN_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** A yyyy-MM-dd date as a UTC day number, or null when it is not a real calendar date. */
function dayNumber(value: string | null | undefined): number | null {
  const match = value ? PLAIN_DATE.exec(value.trim()) : null
  if (!match) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const ms = Date.UTC(year, month - 1, day)
  const back = new Date(ms)
  // Date.UTC rolls 2026-02-31 over into March; a date that does not survive the round trip is not one.
  if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) {
    return null
  }
  return ms / 86_400_000
}

/**
 * Days in a window whose end is EXCLUSIVE, as the jobs define it — so 2026-09-01 to 2026-09-02 is one day.
 * Null when either bound is not a plain date. Zero or less is returned as is: the server refuses it, and
 * the form should say so rather than hide it.
 */
export function windowLengthDays(start: string | null | undefined, end: string | null | undefined): number | null {
  const s = dayNumber(start)
  const e = dayNumber(end)
  return s === null || e === null ? null : e - s
}

/** The window-length readout for the run form, or null for a job with no date window. */
export interface WindowReadout {
  days: number | null
  defaultDays: number | null
  /** True when the window asked for is longer than the one the job defaults to. */
  longerThanDefault: boolean
}

export function windowReadout(
  parameters: EtlJobParameter[],
  values: Record<string, string>,
  defaults: Record<string, string>
): WindowReadout | null {
  const names = new Set(parameters.map((p) => p.name))
  if (!names.has(WINDOW_START) || !names.has(WINDOW_END)) return null
  const days = windowLengthDays(values[WINDOW_START], values[WINDOW_END])
  const defaultDays = windowLengthDays(defaults[WINDOW_START], defaults[WINDOW_END])
  return {
    days,
    defaultDays,
    longerThanDefault: days !== null && defaultDays !== null && days > defaultDays,
  }
}

/** "1 day", "31 days". */
export function daysText(days: number): string {
  return `${days} ${Math.abs(days) === 1 ? 'day' : 'days'}`
}

/** The categories the worker's resume rule and one-at-a-time lock apply to. */
export function isLoadCategory(category: string): boolean {
  return category === 'Warehouse' || category === 'Entity'
}

/** What the run form says about resume, by the rule the catalogue serves. */
export type ResumeNote =
  | { variant: 'none' }
  | { variant: 'no-target'; text: string }
  | { variant: 'reopen'; text: string }
  | { variant: 'live'; text: string }

/**
 * The resume note for a Warehouse or Entity job.
 *
 * Resume matches on orchestrator and mode ONLY, so the target the catalogue names is reopened whatever this
 * request asks for — its entity and its window are ignored, and the unfinished chunks replay with the
 * bounds they were recorded with. Saying nothing would let an operator believe they are loading the window
 * they typed. A live target is different: a same-kind request is dropped by the lock, and the other kind
 * is recorded inside it (checker C11).
 *
 * `none` for any other category, when resume is off, or when an older API serves no rule.
 */
export function resumeNote(category: string, resume: ResumeRule | null | undefined): ResumeNote {
  if (!isLoadCategory(category) || !resume || !resume.enabled) return { variant: 'none' }

  const runKey = resume.targetRunKey ?? resume.targetRunId
  if (runKey == null) {
    return {
      variant: 'no-target',
      text: `Resume is on; no failed or unfinished run in the last ${resume.windowHours}h, so this starts a new run.`,
    }
  }

  if (resume.targetIsLive === true) {
    return {
      variant: 'live',
      text:
        `Run ${runKey} is in progress now. A request of the same kind is dropped by the one-at-a-time lock `
        + '(Hangfire shows it Succeeded and no run is recorded); a request of the other kind (entity vs '
        + `whole-warehouse) is recorded inside ${runKey}, because resume matches on orchestrator and mode only.`,
    }
  }

  const rule = `${resume.windowHours}h window${resume.rulePublished ? '' : ', code default — Automaton has not published its value'}`
  const state = [
    resume.targetStatus ?? 'status not recorded',
    ...(resume.targetIsStale === true ? ['no heartbeat — its worker appears lost'] : []),
    ...(resume.targetStartedAt ? [`started ${agoLabel(resume.targetStartedAt)}`] : []),
  ].join(', ')
  const what = resume.targetEntity ? `a load of ${resume.targetEntity}` : 'a warehouse load'

  return {
    variant: 'reopen',
    text:
      `Resume is on (${rule}). This request will reopen run ${runKey} (${state}) — ${what} — instead of `
      + 'starting a new run, whatever it asks for: resume matches on orchestrator and mode only. Unfinished '
      + 'chunks are replayed with the bounds they were recorded with.',
  }
}

/**
 * The one-at-a-time note: both load methods carry [SkipConcurrentExecution], whose lock is the job's type
 * and method, and a loser is cancelled in OnPerforming — which Hangfire shows as Succeeded, with no run
 * written. Null for a job that is not a load.
 */
export function concurrencyNote(category: string): string | null {
  const kind = category === 'Entity' ? 'entity load' : category === 'Warehouse' ? 'warehouse load' : null
  if (kind === null) return null
  return (
    `Only one ${kind} runs at a time. If another is running when this starts, Hangfire drops this request `
    + '— it shows Succeeded — and no run is recorded.'
  )
}

// ---------------------------------------------------------------------------------------------------------
// Halts
// ---------------------------------------------------------------------------------------------------------

const HALT_REASONS: Record<string, string> = {
  InsufficientDataSpace: 'Insufficient data space',
  InsufficientLogSpace: 'Insufficient log space',
  ManualStop: 'Manual stop',
}

/** A halt reason for reading; an unknown one is shown as the server named it. */
export function haltReasonText(reason: string): string {
  return HALT_REASONS[reason] ?? reason
}

/**
 * Why the server would refuse this job right now, or null when a halt does not stop it.
 *
 * The trigger endpoint refuses every category except the halt's `exemptCategories` (ordinal, as
 * EtlHaltPolicy.IsExempt compares). An older API serves no list and exempted nothing, so an absent list
 * blocks everything — which is what that server would do.
 */
export function haltRefusal(category: string, halt: EtlHaltSummary | null | undefined): string | null {
  if (!halt) return null
  if ((halt.exemptCategories ?? []).includes(category)) return null
  return (
    `ETL is halted (${haltReasonText(halt.reason)} on run ${halt.runKey}). ${category} jobs are refused `
    + 'until an administrator clears the halt.'
  )
}

/** The separator EtlHaltController appends a clearing note after — utils/haltDetail's, re-exported. */
export { HALT_CLEARED_MARKER }

/**
 * A halt's detail split back into the diagnosis and the note appended when it was cleared — utils/haltDetail's
 * split, for a detail the server served. A surface that may receive a WITHHELD detail renders `haltDetailView`
 * instead, which tells withheld from none.
 */
export function splitHaltDetail(detail: string | null | undefined): { diagnosis: string | null; note: string | null } {
  const view = haltDetailView(detail, false)
  return view.kind === 'recorded' ? { diagnosis: view.diagnosis, note: view.note } : { diagnosis: null, note: null }
}

/** What an active-halt row needs; the full halt list and the catalogue's summary both provide it. */
export interface ActiveHaltRow {
  runId: string
  runKey: string
  reason: string
  detail: string | null
  /** The server withheld a recorded detail from this caller (D8). Absent: false. */
  detailWithheld?: boolean
  haltedAt: string | null
  orchestratorName: string | null
}

/**
 * What the halt panel shows.
 *
 * - `failed`: the list could not be read. That says NOTHING about whether ETL is halted, and the
 *   catalogue's banner (its newest halt) is still shown when it has one.
 * - `loading`: the same fallback while the list loads — a known halt is never hidden behind a spinner.
 * - `halted`: every blocking halt, not only the newest, plus the cleared ones as history.
 * - `clear`: nothing blocking; the cleared ones as history.
 */
export type HaltView =
  | { kind: 'failed'; fallback: ActiveHaltRow[]; count: number }
  | { kind: 'loading'; fallback: ActiveHaltRow[]; count: number }
  | { kind: 'halted'; active: EtlHalt[]; cleared: EtlHalt[] }
  | { kind: 'clear'; cleared: EtlHalt[] }

export function haltView(
  halts: EtlHalt[] | undefined,
  isLoading: boolean,
  isError: boolean,
  catalog: EtlJobCatalog | undefined
): HaltView {
  const summary = catalog?.activeHalt ?? null
  const fallback: ActiveHaltRow[] = summary
    ? [{
        runId: summary.runId,
        runKey: summary.runKey,
        reason: summary.reason,
        detail: summary.detail ?? null,
        detailWithheld: summary.detailWithheld === true,
        haltedAt: summary.haltedAt ?? null,
        orchestratorName: null,
      }]
    : []
  const count = Math.max(catalog?.activeHaltCount ?? 0, fallback.length)

  if (isError) return { kind: 'failed', fallback, count }
  if (isLoading || !halts) return { kind: 'loading', fallback, count }

  const active = halts.filter((h) => h.acknowledgedAt == null)
  const cleared = halts.filter((h) => h.acknowledgedAt != null)
  return active.length > 0 ? { kind: 'halted', active, cleared } : { kind: 'clear', cleared }
}

/**
 * What still runs during a halt, as the catalogue serves it.
 *
 * `unknown` when the catalogue names no active halt (it has not caught up with one the list shows). An
 * ABSENT list on a halt is an older API, which exempted nothing — so it reads as none.
 */
export type ExemptSummary =
  | { kind: 'unknown' }
  | { kind: 'none' }
  | { kind: 'some'; categories: string[]; includesDiagnostics: boolean }

export function exemptSummary(catalog: EtlJobCatalog | undefined): ExemptSummary {
  const halt = catalog?.activeHalt
  if (!halt) return { kind: 'unknown' }
  const categories = halt.exemptCategories ?? []
  if (categories.length === 0) return { kind: 'none' }
  return { kind: 'some', categories, includesDiagnostics: categories.includes('Diagnostics') }
}

/**
 * What an uncleared halt stops, in one sentence — built from the categories the SERVER says a halt lets
 * through (`activeHalt.exemptCategories`, EtlHaltPolicy.ExemptCategories), never from a list kept here, so
 * the Overview, Execution History and the Scheduler cannot drift from the server or from each other. Until
 * the catalogue has answered with an active halt it says no more than that the halt blocks runs.
 */
export function haltConsequenceText(catalog: EtlJobCatalog | undefined): string {
  const exempt = exemptSummary(catalog)
  switch (exempt.kind) {
    case 'unknown':
      return 'Blocks runs until an administrator clears it.'
    case 'none':
      return 'Blocks every run until an administrator clears it.'
    case 'some':
      return `Blocks every run except ${listText(exempt.categories)} jobs until an administrator clears it.`
  }
}

/** "A", "A and B", "A, B and C". */
function listText(items: readonly string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/**
 * True when the polled halt list and the (unpolled) catalogue disagree about how many halts block runs --
 * the cue to re-read the catalogue, whose halt summary is what gates the Run buttons.
 */
export function catalogBehindHalts(halts: EtlHalt[] | undefined, catalog: EtlJobCatalog | undefined): boolean {
  if (!halts || !catalog) return false
  const blocking = halts.filter((h) => h.acknowledgedAt == null).length
  const served = catalog.activeHaltCount ?? (catalog.activeHalt ? 1 : 0)
  return blocking !== served
}
